// A portable workflow (format `ocs-workflow-export/1`, docs/13 "Portable workflows"): the graph,
// requirements, skills and changelog of one published version, as OCS exports it. The graph's
// shapes follow docs/12 "The graph" and web/src/catalog/types.ts, which the admin web edits.

export const PORTABLE_FORMAT = "ocs-workflow-export/1";

export type StepOutcome = "succeeded" | "failed" | "timed_out" | "skipped";

export interface ToolCapability {
  tool: string;
  actions: string[];
  requires_approval?: boolean;
}

export interface BrokerGrant {
  tool: string;
  action: string;
  requirement: string;
}

export interface PreExecution {
  language: "python" | "bash";
  source: string;
  timeout_seconds?: number;
  broker?: BrokerGrant[];
}

export interface PromptNode {
  kind: "prompt";
  id: string;
  name?: string;
  prompt: string;
  model?: string;
  timeout_minutes?: number;
  needs_docker?: boolean;
  max_visits?: number;
  tools?: ToolCapability[];
  outputs?: string[];
  cli?: string;
  pre_execution?: PreExecution;
}

export interface ApprovalNode {
  kind: "approval";
  id: string;
  name?: string;
  question: string;
  options: string[];
  max_visits?: number;
}

export interface OrchestrateNode {
  kind: "orchestrate";
  id: string;
  name?: string;
  max_parallel?: number;
  max_visits?: number;
}

export interface EndNode {
  kind: "end";
  id: string;
  name?: string;
  result: "succeeded" | "failed";
}

export type GraphNode = PromptNode | ApprovalNode | OrchestrateNode | EndNode;
export type NodeKind = GraphNode["kind"];

export type OutputTest = { key: string; equals: string } | { key: string; in: string[] } | { key: string; exists: boolean };

export type Condition = { outcome: StepOutcome } | { output: OutputTest } | { answer: string } | "otherwise";

export interface GraphEdge {
  from: string;
  to: string;
  when: Condition;
}

export interface Graph {
  start: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export type Requirement =
  | { kind: "credential"; name?: string; credential_kind: string; required: boolean; platform_default?: boolean }
  | { kind: "oauth"; provider: string; required: boolean }
  | { kind: "isolation"; level: "vm" };

export interface SkillFile {
  path: string;
  content: string;
}

export interface Skill {
  slug: string;
  name: string;
  description: string;
  files: SkillFile[];
}

export interface PortableWorkflow {
  format: string;
  task_type?: { slug: string; name: string; description?: string; icon?: string | null };
  workflow: { slug: string; name: string; description: string; icon?: string | null };
  skills: Skill[];
  version: {
    changelog?: string;
    requirements: Requirement[];
    definition: Graph;
    skills: string[];
  };
}

/** "Plan review", or the id when the step has no name. */
export function stepName(node: GraphNode): string {
  return node.name?.trim() || node.id;
}

/** How an edge's condition reads on the canvas: "succeeded", "verdict = pass", "answer: approve". */
export function describe(when: Condition): string {
  if (when === "otherwise") return "otherwise";
  if ("outcome" in when) return when.outcome.replace("_", " ");
  if ("answer" in when) return when.answer;
  const test = when.output;
  if ("equals" in test) return `${test.key} = ${test.equals}`;
  if ("in" in test) return `${test.key} ∈ ${test.in.join(" | ")}`;
  return test.exists ? `${test.key} set` : `no ${test.key}`;
}
